const RECENTLY_VIEWED_STORAGE_KEY = 'dmm_konchu:recently-viewed';
const RECENTLY_VIEWED_MOBILE_BREAKPOINT = 576;
const RECENTLY_VIEWED_SLIDE_GAP = 15;

function getRecentlyViewedProducts() {
  try {
    const stored = localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function getRootUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

function buildProductSectionUrl(handle, cardSectionId, cardQuery, useSectionsParam) {
  const root = getRootUrl();
  const param = useSectionsParam ? 'sections' : 'section_id';
  return `${root}products/${encodeURIComponent(handle)}?${param}=${cardSectionId}&${cardQuery}`;
}

function parseSectionHtml(responseText, sectionId) {
  try {
    const data = JSON.parse(responseText);
    if (data?.[sectionId]) return data[sectionId];
  } catch {
    /* not JSON */
  }

  const doc = new DOMParser().parseFromString(responseText, 'text/html');
  return (
    doc.querySelector(`#shopify-section-${sectionId}`)?.innerHTML ||
    doc.querySelector(`[data-recently-viewed-card]`)?.closest('.shopify-section')?.innerHTML ||
    doc.querySelector('[data-recently-viewed-card]')?.outerHTML ||
    ''
  );
}

function extractCardHtml(sectionHtml) {
  if (!sectionHtml) return '';

  const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const card = doc.querySelector('[data-recently-viewed-card]');
  return card ? card.outerHTML : '';
}

function isMobileSliderViewport() {
  const w = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  return w < RECENTLY_VIEWED_MOBILE_BREAKPOINT;
}

if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    resizeHandler = undefined;
    scrollHandler = undefined;

    connectedCallback() {
      this.classList.add('recently-viewed-products--loading');
      this.resizeHandler = () => this.updateMobileSlider();
      window.addEventListener('resize', this.resizeHandler);
      this.loadProducts();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.resizeHandler);
      this.unbindSliderControls();
    }

    buildCardQueryString(skipStyles) {
      return [
        `image_ratio=${encodeURIComponent(this.dataset.imageRatio || 'adapt')}`,
        `image_shape=${encodeURIComponent(this.dataset.imageShape || 'default')}`,
        `show_secondary_image=${this.dataset.showSecondaryImage === 'true'}`,
        `show_vendor=${this.dataset.showVendor === 'true'}`,
        `show_rating=${this.dataset.showRating === 'true'}`,
        `skip_styles=${skipStyles}`,
      ].join('&');
    }

    hideSection() {
      this.closest('.recently-viewed-products-section')?.remove();
    }

    getDisplayItems(limit) {
      const currentProductId = document.querySelector('product-info[data-product-id]')?.dataset.productId;
      const items = getRecentlyViewedProducts();
      const filtered = items.filter((item) => String(item.id) !== String(currentProductId));

      return (filtered.length ? filtered : items).slice(0, limit);
    }

    async fetchCardHtml(handle, cardSectionId, cardQuery) {
      const urls = [
        buildProductSectionUrl(handle, cardSectionId, cardQuery, true),
        buildProductSectionUrl(handle, cardSectionId, cardQuery, false),
      ];

      for (const url of urls) {
        try {
          const response = await fetch(url);
          if (!response.ok) continue;

          const text = await response.text();
          const sectionHtml = parseSectionHtml(text, cardSectionId);
          const cardHtml = extractCardHtml(sectionHtml);
          if (cardHtml) return cardHtml;
        } catch {
          /* try next URL */
        }
      }

      return '';
    }

    getScrollStep() {
      const slide = this.querySelector('[data-recently-viewed-grid] > .grid__item');
      if (!slide) return 0;
      return slide.offsetWidth + RECENTLY_VIEWED_SLIDE_GAP;
    }

    updateNavState() {
      const track = this.querySelector('[data-recently-viewed-track]');
      const prev = this.querySelector('[data-recently-viewed-prev]');
      const next = this.querySelector('[data-recently-viewed-next]');
      if (!track || !prev || !next) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      const slop = 2;

      prev.disabled = track.scrollLeft <= slop;
      next.disabled = track.scrollLeft >= maxScroll - slop;
    }

    scrollTrack(direction) {
      const track = this.querySelector('[data-recently-viewed-track]');
      if (!track) return;

      const step = this.getScrollStep();
      if (!step) return;

      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    unbindSliderControls() {
      const track = this.querySelector('[data-recently-viewed-track]');
      const prev = this.querySelector('[data-recently-viewed-prev]');
      const next = this.querySelector('[data-recently-viewed-next]');

      if (track && this.scrollHandler) {
        track.removeEventListener('scroll', this.scrollHandler);
      }
      if (prev && this.prevHandler) {
        prev.removeEventListener('click', this.prevHandler);
      }
      if (next && this.nextHandler) {
        next.removeEventListener('click', this.nextHandler);
      }

      this.scrollHandler = undefined;
      this.prevHandler = undefined;
      this.nextHandler = undefined;
    }

    bindSliderControls() {
      this.unbindSliderControls();

      const track = this.querySelector('[data-recently-viewed-track]');
      const prev = this.querySelector('[data-recently-viewed-prev]');
      const next = this.querySelector('[data-recently-viewed-next]');
      if (!track || !prev || !next) return;

      this.scrollHandler = () => this.updateNavState();
      this.prevHandler = () => this.scrollTrack(-1);
      this.nextHandler = () => this.scrollTrack(1);

      track.addEventListener('scroll', this.scrollHandler, { passive: true });
      prev.addEventListener('click', this.prevHandler);
      next.addEventListener('click', this.nextHandler);

      this.updateNavState();
    }

    updateMobileSlider() {
      const enable = isMobileSliderViewport() && this.classList.contains('recently-viewed-products--loaded');

      this.classList.toggle('recently-viewed-products--mobile-slider', enable);

      if (enable) {
        this.bindSliderControls();
        this.updateNavState();
      } else {
        this.unbindSliderControls();
        const track = this.querySelector('[data-recently-viewed-track]');
        if (track) track.scrollLeft = 0;
      }
    }

    async loadProducts() {
      const limit = parseInt(this.dataset.limit, 10) || 4;
      const items = this.getDisplayItems(limit);
      const grid = this.querySelector('[data-recently-viewed-grid]');
      const cardSectionId = this.dataset.cardSectionId;

      if (!items.length || !grid || !cardSectionId) {
        this.hideSection();
        return;
      }

      const cards = await Promise.all(
        items.map(async (item, index) => {
          const cardQuery = this.buildCardQueryString(index > 0);
          return this.fetchCardHtml(item.handle, cardSectionId, cardQuery);
        })
      );

      const validCards = cards.filter(Boolean);

      if (!validCards.length) {
        this.hideSection();
        return;
      }

      grid.innerHTML = validCards.join('');
      this.classList.remove('recently-viewed-products--loading');
      this.classList.add('recently-viewed-products--loaded');
      this.removeAttribute('aria-hidden');

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(this);
      }

      this.updateMobileSlider();
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
